import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core';
import { JuiControlSize, JuiValidationEvents } from '../../../shared/form-controls/jui-form-control.model';
import { IconSize, IconVariant } from '../../jui-icon/jui-icon.model';
import {
  renderTooltipOnBottom,
  renderTooltipOnLeft,
  renderFormError
} from '../../../shared/form-controls/jui-form-control.utils';

@Component({
  tag: 'jui-input',
  styleUrl: 'jui-input.scss',
  shadow: true
})
export class JuiInput implements ComponentInterface {
  @Element() el: HTMLElement;

  /**
   * The input type. Defaults to text.
   */
  @Prop() type = 'text';

  /**
   * The input label.
   */
  @Prop() label: string;

  /**
   * The form name for the input control.
   */
  @Prop() name = '';

  /**
   * The input placeholder. Defaults to the label.
   */
  @Prop() placeholder = '';

  /**
   * The input value.
   */
  @Prop({ attribute: 'value', reflect: true, mutable: true })
  value: string;

  /**
   * The errors for the input
   */
  @Prop() errors: boolean | { [key: string]: string };

  /**
   * The validation messages for the input
   */
  @Prop() messages: string | { [key: string]: string };

  /**
   * Marking the input field as readonly. Defaults to false
   */
  @Prop({ attribute: 'readonly', reflect: true })
  readonly = false;

  /**
   * Marking the input field as disabled. Defaults to false
   */
  @Prop() disable: boolean;

  /**
   * Marking the input field as required. Defaults to false
   */
  @Prop() required = false;

  /**
   * Optionally provide an icon to be displayed next to the input field
   */
  @Prop() icon = '';

  /**
   * Set the max length of the input
   */
  @Prop() maxlength?: number | null = null;

  /**
   * Set the min length of the input
   */
  @Prop() minlength?: number | null = null;

  /**
   * Optionally autofocus the input. Defaults to false
   */
  @Prop() autofocus = false;

  /**
   * Set the input field to be clearable
   */
  @Prop() clearable = false;

  /**
   * Set the input field to be transitionable
   */
  @Prop() transitional = false;

  /**
   * Set the size of the input. Options are 'md', 'lg' or 'sm'. Defaults to 'md'
   */
  @Prop() size: JuiControlSize = JuiControlSize.md;

  /**
   * The event to trigger validation check
   */
  @Prop() validation: JuiValidationEvents = JuiValidationEvents.onLoad;

  /**
   * Optional data-qa string
   */
  @Prop() dataqastring: string = null;

  /**
   * Optional help string
   */
  @Prop() help: string = null;

  /**
   * Optional tooltip string
   */
  @Prop() tooltip: string = null;

  /**
   * Output event emitted when change is fired
   */
  @Event() public juiChange: EventEmitter<string>;

  /**
   * Output event emitted when input is fired
   */
  @Event() public juiInput: EventEmitter<string>;

  /**
   * Output event emitted when blur is fired
   */
  @Event() public juiBlur: EventEmitter<string>;

  /**
   * Output event emitted when focus is fired
   */
  @Event() public juiFocus: EventEmitter<string>;

  @State()
  private errorMessages = null;

  @State()
  private inputLength = 0;

  @State()
  private touched = false;

  @State()
  private focused = false;

  @State()
  private isClearing = false;

  @State()
  private tooltipLeft = false;

  @State()
  private tooltipLeftBottom = false;

  private get showClearIcon(): boolean {
    return this.clearable && !!this.value.length;
  }

  componentDidLoad(): void {
    if (this.autofocus) {
      this.focusControl();
    }
  }

  componentDidRender(): void {
    if (this.tooltip) {
      this.renderTooltips();
    }
  }

  componentWillLoad(): void {
    if (this.value) {
      this.inputLength = this.value.length;
    }

    if (!this.maxlength || this.maxlength === 0) {
      this.maxlength = 200;
    }

    if (this.validation === JuiValidationEvents.onLoad) {
      this.touched = true;
      this.renderError();
    }
  }

  componentWillUpdate(): void {
    this.renderError();
  }

  private onInput(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
    this.inputLength = this.value.length;
    this.juiInput.emit(this.value);
  }

  private onChange(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
    this.juiChange.emit(this.value);
  }

  private onBlur(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
    this.focused = false;
    this.juiBlur.emit(this.value);
  }

  private onFocus(event: Event): void {
    this.touched = true;
    this.focused = true;
    this.value = (event.target as HTMLInputElement).value;
    this.juiFocus.emit(this.value);
  }

  private clearInput = (evt: Event) => {
    this.isClearing = true;
    this.value = '';
    this.inputLength = 0;
    this.juiInput.emit(this.value);
    if (evt.type === 'click') {
      setTimeout((): void => {
        this.focusControl();
        this.isClearing = false;
      }, 30);
    } else {
      this.focusControl();
      this.isClearing = false;
    }
  };

  private focusControl(): void {
    const inputEl = this.el.shadowRoot.querySelector('.jui-control__input') as HTMLInputElement;
    inputEl.focus();
  }

  private getIconSize(): IconSize {
    switch (this.size) {
      case JuiControlSize.md:
        return IconSize.sm;
      case JuiControlSize.lg:
        return IconSize.lg;
      case JuiControlSize.sm:
        return IconSize.xs;
    }
  }

  render() {
    const errorFragment = this.errorMessages ? this.errorMessages : null;
    const dataQaTagLabel = this.dataqastring ? `${this.dataqastring}_formFieldLabel` : null;
    const dataQaTagInput = this.dataqastring ? `${this.dataqastring}_formField` : null;

    const helpText = this.help && !this.transitional ? <div class="jui-control__help">{this.help}</div> : null;

    const tooltip = this.tooltip ? (
      <div
        class={{
          'jui-control__tooltip': true,
          [`jui-control__tooltip--input-${this.size}`]: true
        }}
      >
        <jui-icon
          class={{ 'jui-control__tooltip-icon': true }}
          name="help_outline"
          cursor="help"
          color={IconVariant.info}
          size={IconSize.xs}
          tabindex={-1}
        ></jui-icon>
        <span
          class={{
            'jui-control__tooltip-text': true,
            'jui-control__tooltip-text--left': this.tooltipLeft && !this.tooltipLeftBottom,
            'jui-control__tooltip-text--left-bottom': this.tooltipLeftBottom
          }}
        >
          {this.tooltip}
        </span>
      </div>
    ) : null;
    const icon = this.icon ? (
      <div
        class={{
          'jui-control__icon': true
        }}
      >
        <jui-icon name={this.icon} color={IconVariant.current} size={this.getIconSize()} isButton={false} />
      </div>
    ) : null;
    const characterCount = this.maxlength ? (
      <div>
        {this.inputLength}/{this.maxlength}
      </div>
    ) : null;
    const clearableIcon = this.showClearIcon ? (
      <div
        class={{
          'jui-control__reset': true,
          [`jui-control__reset--${this.size}`]: true
        }}
      >
        <jui-icon
          name="clear"
          onClick={event => this.clearInput(event)}
          color={IconVariant.current}
          size={this.getIconSize()}
        />
      </div>
    ) : null;

    return (
      <Host data-qa={this.dataqastring || null}>
        <div
          class={{
            'jui-control': true,
            [`jui-control--${this.size}`]: true,
            'jui-control--transitional': !!this.transitional,
            'jui-control--has-focus': !!this.isClearing,
            'jui-control--no-label': !this.label
          }}
        >
        {this.label ? (
          <label
            {...(dataQaTagLabel && { 'data-qa': dataQaTagLabel })}
            class={{
              'jui-control__label': true,
              'jui-control__label--has-focus': !!this.focused || !!this.isClearing,
              'jui-control__label--has-value': !!this.value,
              'jui-control__label--has-icon': !!this.icon,
              'jui-control__label--disabled': !!this.disable,
              'jui-control__label--narrower': !!this.tooltip
            }}
          >
            <div
              class={{
                'jui-control__label-text': true
              }}
            >
              {this.required ? `${this.label} *` : this.label}
              {characterCount}
            </div>
          </label>
        ) : null}
        {helpText}
        {tooltip}
        <input
          {...(dataQaTagInput && { 'data-qa': dataQaTagInput })}
          class={{
            'jui-control__input': true,
            'jui-control__input--has-value': !!this.value,
            'jui-control__input--clearable': !!this.clearable,
            'jui-control__input--has-icon': !!this.icon,
            'jui-control__input--invalid': (!!this.errors && this.touched) || (!!this.errors && this.focused),
            'jui-control__input--has-focus': !!this.isClearing || !!this.focused
          }}
          onKeyDown={event => {
            if (event.key === 'Escape' && this.clearable) {
              this.clearInput(event);
            }
          }}
          name={this.name}
          type={this.type}
          value={this.value}
          placeholder={this.placeholder.length ? this.placeholder : this.label}
          maxLength={this.maxlength}
          minLength={!isNaN(this.minlength) ? this.minlength : undefined}
          required={this.required}
          disabled={this.disable}
          readOnly={this.readonly}
          onInput={this.onInput.bind(this)}
          onChange={this.onChange.bind(this)}
          onFocusout={this.onBlur.bind(this)}
          onFocusin={this.onFocus.bind(this)}
        />
        {icon}
        {clearableIcon}
        {errorFragment}
        </div>
      </Host>
    );
  }

  private renderTooltips(): void {
    this.tooltipLeft = renderTooltipOnLeft(this.el);
    if (this.tooltipLeft) {
      this.tooltipLeftBottom = renderTooltipOnBottom(this.el);
    }
  }

  private renderError() {
    this.errorMessages = renderFormError(this.errors, this.messages);
  }
}
